import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type AxiosError } from "axios";
import { Status } from "core/models/error";

type StatusCode = keyof typeof Status;
type StatusType = typeof Status[StatusCode];

type ExceptionDetails = {
	message?: string;
	stackTrace?: string;
};

type ErrorResponse = {
	exception?: ExceptionDetails;
	errors?: Record<string, string[]>;
};

export type ApiError = {
	message: string;
	errors?: Record<string, string[]>;
	stack?: string;
	type?: StatusType;
};

type PaginationMetadata = {
	Page: number;
	Total: number;
	Take: number;
	Skip: number;
	Pages: number;
};

type PaginatedResponse<T> = {
	values: T;
	pagination: PaginationMetadata;
};

type RequestConfig = AxiosRequestConfig & {
	paginated?: boolean;
};

type AuthTokenProvider = () => Promise<string | undefined> | string | undefined;

export default class ApiClient {
	private client: AxiosInstance;
	private authTokenProvider?: AuthTokenProvider;

	constructor(baseURL: string) {
		this.client = axios.create({
			baseURL,
			timeout: import.meta.env.MODE === "development" ? 0 : 25000, // no timeout or 25 seconds
			headers: {
				"Content-Type": "application/json"
			}
		});

		this._reqInterceptor();
		this._resInterceptor();
	}

	private _reqInterceptor(): void {
		this.client.interceptors.request.use(
			async (config) => {
				if (this.authTokenProvider) {
					const token = await this.authTokenProvider();
					if (token) {
						config.headers = config.headers ?? {};
						config.headers.Authorization = `Bearer ${token}`;
					}
				}
				return config;
			},
			this._handleError
		);
	}

	private _resInterceptor(): void {
		this.client.interceptors.response.use(
			this._handleResponse,
			this._handleError
		);
	}

	private _handleResponse<T>(response: AxiosResponse<T>): T | PaginatedResponse<T> {
		const pagination = response.headers["x-pagination"];
		if (pagination) return { values: response.data, pagination: JSON.parse(pagination) as PaginationMetadata };
		return response.data;
	}

	private _handleError(error: AxiosError<ErrorResponse>): Promise<ApiError> {
		let err: ApiError;
		const response = error.response;

		if (response) {
			const exception = response.data?.exception || {};
			const statusCode = response.status as StatusCode;

			err = {
				message: exception.message || error.message || "API Error",
				errors: response.data?.errors,
				stack: exception.stackTrace,
				type: Status[statusCode]
			};
		} else if (error.request) {
			// Request was made but no response received
			err = {
				message: "Network error - no response received"
			};
		} else {
			// Something else happened
			err = {
				message: error.message || "Unknown error occurred"
			};
		}

		return Promise.reject(err);
	}

	setAuthTokenProvider(provider?: AuthTokenProvider) {
		this.authTokenProvider = provider;
	}

	async get<T = unknown>(path: string = "", config?: AxiosRequestConfig): Promise<T> {
		const response = await this.client.get<T>(path, config);
		return response as T;
	}

	async post<T = unknown>(path: string, data?: unknown, config?: Omit<RequestConfig, "paginated"> & { paginated?: false }): Promise<T>;
	async post<T = unknown>(path: string, data?: unknown, config?: Omit<RequestConfig, "paginated"> & { paginated: true }): Promise<PaginatedResponse<T>>;
	async post<T = unknown>(path: string = "", data?: unknown, config?: RequestConfig): Promise<T> {
		const response = await this.client.post<T>(path, data, config);
		return response as T;
	}

	async put<T = unknown>(path: string = "", data?: unknown, config?: AxiosRequestConfig): Promise<T> {
		const response = await this.client.put<T>(path, data, config);
		return response as T;
	}

	async patch<T = unknown>(path: string = "", data?: unknown, config?: AxiosRequestConfig): Promise<T> {
		const response = await this.client.patch<T>(path, data, config);
		return response as T;
	}

	async delete<T = unknown>(path: string = "", config?: AxiosRequestConfig): Promise<T> {
		const response = await this.client.delete<T>(path, config);
		return response as T;
	}
}